import { Title } from "@radix-ui/react-dialog";
import { SEARCH, SEE_ALL_RESULTS } from "@RHCommerceDev/utils/constants";
import { cn } from "@RHCommerceDev/utils/cn";
import { useSearch } from "@RHCommerceDev/hooks/useSearch";
import RHCloseIcon from "icons/RHCloseIcon";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerTrigger
} from "@RHCommerceDev/unstyled-ui-components";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import BrandLogo from "@RHCommerceDev/container-rhr-header/UtilityBar/BrandLogo";
import React, { useCallback } from "react";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { useCookiesWithPermission } from "hooks/useCookiesWithPermission";
import SearchIcon from "@RHCommerceDev/icon-header-search";

const SearchDrawer = () => {
  const { term, setTerm, executeSearch } = useSearch();

  const { setStorageValueWrapper } = useCookiesWithPermission();

  const handleSearchExecute = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      executeSearch();
    },
    [executeSearch, setStorageValueWrapper, term]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      //analytics when input is changed
      if (!processEnvServer) {
        setStorageValueWrapper({
          storageKey: "analytics-access",
          value: "typeahead"
        });
      }

      setTerm(e.target.value);
    },
    [setStorageValueWrapper, setTerm]
  );

  const DrawerHeader = () => {
    return (
      <Title asChild>
        <div
          className={cn(
            "relative flex justify-center",
            "pt-3.5",
            "h-[82px] shrink-0", // `shrink-0` helps to apply height
            "bg-rh-mocha text-white"
          )}
        >
          <DrawerClose
            className={cn(
              "h-[22px] w-[22px]",
              "absolute left-0 top-1/2 -translate-y-1/2",
              "p-0 text-inherit"
            )}
          >
            <RHCloseIcon className="mx-auto my-auto text-inherit" />
          </DrawerClose>
          <BrandLogo />
        </div>
      </Title>
    );
  };

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <button
          id="search-icon"
          aria-label={SEARCH}
          className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center p-0 text-inherit focus:outline-none"
        >
          <SearchIcon className="h-[18.394px] w-[17.703px]" />
        </button>
      </DrawerTrigger>

      <DrawerPortal>
        <DrawerOverlay />

        <DrawerContent
          direction="left"
          className={cn("w-full bg-rh-mocha px-4 text-white")}
        >
          <DrawerHeader />
          <form
            onSubmit={handleSearchExecute}
            className="flex flex-col items-center justify-center gap-12"
          >
            <div
              className={cn(
                "relative flex w-full items-center justify-center",
                "gap-2",
                "pb-5 pt-11",
                "after:absolute after:bottom-0 after:h-px after:w-full after:bg-rh-warm-latte after:content-['']"
              )}
            >
              <div className="flex h-6 w-6 items-center justify-center">
                <SearchIcon />
              </div>
              <input
                id="site-search-input"
                autoFocus
                value={term}
                onChange={handleInputChange}
                placeholder="Search"
                className={cn(
                  "bg-inherit p-0 text-inherit",
                  "w-full focus:outline-none",
                  "font-rhc-roman text-base font-normal leading-normal tracking-[0.015em]",
                  "placeholder:font-rhc placeholder:font-thin placeholder:text-rh-warm-latte",
                  "caret-white"
                )}
              />
            </div>

            {term.trim() && (
              <button
                type="submit"
                className={cn(
                  "flex items-center justify-center",
                  "h-12 w-full",
                  "border-[0.5px] border-solid border-white",
                  "p-0 text-inherit"
                )}
              >
                <Typography
                  fontFamily="rhSans"
                  fontWeight="light"
                  className="inline text-center text-[11px] uppercase leading-[1.2em] tracking-[0.04em] text-inherit"
                  variant="default"
                >
                  {SEE_ALL_RESULTS}
                </Typography>
              </button>
            )}
          </form>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  );
};

export default SearchDrawer;
